/**
 * Add a test to a UAT section (Bill 2026-11-04: testers can add UAT items). The next ID in the section is given by
 * the server (UC-62 after UC-61); the item starts Not Started and the addition is recorded under your name.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { PilotItem, PilotItemInput, PilotSection } from './pilotApi';
import { columnLabel } from './pilotModel';

export interface AddItemDialogProps {
  section: PilotSection | null;
  onClose: () => void;
  onAdded: (item: PilotItem) => void;
}

const EMPTY: PilotItemInput = { area: '', title: '', steps: '', channel: 'Both', testerGroup: '', accountTypes: '', notes: '' };

export function AddItemDialog({ section, onClose, onAdded }: AddItemDialogProps): JSX.Element | null {
  const client = useApiClient();
  const [f, setF] = useState<PilotItemInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (section) { setF(EMPTY); setErr(null); } }, [section]);
  if (!section) return null;
  const set = (k: keyof PilotItemInput, v: string): void => setF((x) => ({ ...x, [k]: v }));
  const canSave = !!(f.title?.trim() || f.steps?.trim()) && !busy;

  async function save(): Promise<void> {
    if (!section || !canSave) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await client.http.post<{ item: PilotItem }>(`/v3/rop/pilot/sections/${section.id}/items`, { body: f });
      onAdded(r.item);
    } catch (e: unknown) {
      setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const dirty = Object.entries(f).some(([k, v]) => v !== (EMPTY as Record<string, string | undefined>)[k]);
  return (
    <ModalShell open onClose={onClose} size="md" title={`Add a test to ${section.name}`} confirmClose isDirty={dirty}
      footer={(
        <div className="pilot-actions">
          {err && <span className="pilot-muted" role="alert" style={{ color: 'var(--err-600)' }}>{err}</span>}
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void save()} disabled={!canSave}>{busy ? 'Adding…' : 'Add test'}</Button>
        </div>
      )}>
      <div className="pilot-form">
        <label className="pilot-field">
          {columnLabel(section, 'area', 'Area')}
          <input value={f.area} maxLength={200} onChange={(e) => set('area', e.target.value)} />
        </label>
        <label className="pilot-field">
          {columnLabel(section, 'channel', 'Channel')}
          <select value={f.channel} onChange={(e) => set('channel', e.target.value)}>
            <option value="Both">Both</option>
            <option value="In-Person">In-Person</option>
            <option value="Online">Online</option>
          </select>
        </label>
        <label className="pilot-field pilot-field--wide">
          {columnLabel(section, 'title', 'Test item')} <em>(what is being tested)</em>
          <input value={f.title} maxLength={400} onChange={(e) => set('title', e.target.value)} />
        </label>
        <label className="pilot-field pilot-field--wide">
          {columnLabel(section, 'steps', 'Test steps and expected result')}
          <textarea value={f.steps} maxLength={8000} placeholder="What to do. Expected: what should happen."
            onChange={(e) => set('steps', e.target.value)} />
        </label>
        <label className="pilot-field">
          {columnLabel(section, 'testerGroup', 'Tester group')}
          <input value={f.testerGroup} maxLength={128} onChange={(e) => set('testerGroup', e.target.value)} />
        </label>
        <label className="pilot-field">
          {columnLabel(section, 'accountTypes', 'Account types')}
          <input value={f.accountTypes} maxLength={256} onChange={(e) => set('accountTypes', e.target.value)} />
        </label>
        <label className="pilot-field pilot-field--wide">
          Notes
          <textarea value={f.notes} maxLength={8000} onChange={(e) => set('notes', e.target.value)} />
        </label>
      </div>
    </ModalShell>
  );
}
